import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, query, where, getCountFromServer, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';
import { SUITE_APPS, COMING_SOON_APPS, userCanAccessApp } from './constants/appCatalog';
import './SuiteDashboard.css';

function SuiteDashboard({ user, userProfile, orgSettings }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState({
    openDefects: 0,
    loading: true
  });
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  // If the user was redirected here by AppAccessGate (typed a URL they
  // don't have access to), show a one-time flash message.
  const [accessFlash, setAccessFlash] = useState(location.state?.deniedAppId || null);

  const role = userProfile?.role || 'Unassigned';
  const isAdmin = role === 'Admin';
  const hasProfile = !!userProfile?.role;

  // Set page title
  useEffect(() => {
    document.title = 'Dashboard - Wixon & Cross Desk Suite';
  }, []);

  useEffect(() => {
    if (!hasProfile) {
      setStats({ openDefects: 0, loading: false });
      return;
    }

    // Get count of open defects
    const fetchStats = async () => {
      try {
        const q = query(
          collection(db, 'defects'),
          where('status', '==', 'Open')
        );
        const snapshot = await getCountFromServer(q);
        setStats({ openDefects: snapshot.data().count, loading: false });
      } catch (err) {
        console.error('Error fetching stats:', err);
        setStats({ openDefects: 0, loading: false });
      }
    };

    fetchStats();
  }, [hasProfile]);

  // Listen for unread messages — both DMs and groups (Build 24: matches Desktop.js).
  // DMs use the legacy `read: false` flag; groups use per-user `lastReadAt`
  // on the conversation document.
  useEffect(() => {
    if (!user) return;

    let unreadDms = 0;
    let unreadGroups = 0;

    const dmQuery = query(
      collection(db, 'messages'),
      where('toUserId', '==', user.uid),
      where('read', '==', false)
    );
    const unsubDm = onSnapshot(dmQuery, (snap) => {
      unreadDms = snap.docs.length;
      setUnreadMessageCount(unreadDms + unreadGroups);
    });

    const conversationsState = { byId: {} };
    const groupMessagesState = { docs: [] };

    const recomputeGroups = () => {
      const docs = groupMessagesState.docs;
      let total = 0;
      for (const conv of Object.values(conversationsState.byId)) {
        const lastReadMs = conv.lastReadAt?.[user.uid]?.toMillis?.() ?? 0;
        for (const msg of docs) {
          if (msg.conversationId !== conv.id) continue;
          if (msg.fromUserId === user.uid) continue;
          const created = msg.createdAt?.toMillis?.() ?? 0;
          if (created > lastReadMs) total++;
        }
      }
      unreadGroups = total;
      setUnreadMessageCount(unreadDms + unreadGroups);
    };

    const convQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid)
    );
    const unsubConv = onSnapshot(convQuery, (snap) => {
      const byId = {};
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.isGroup) byId[d.id] = { id: d.id, ...data };
      });
      conversationsState.byId = byId;
      recomputeGroups();
    });

    const groupMsgQuery = query(
      collection(db, 'messages'),
      where('participants', 'array-contains', user.uid),
      where('isGroup', '==', true)
    );
    const unsubGroupMsg = onSnapshot(groupMsgQuery, (snap) => {
      groupMessagesState.docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      recomputeGroups();
    });

    return () => {
      unsubDm();
      unsubConv();
      unsubGroupMsg();
    };
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      alert('Error signing out.');
    }
  };

  // Build the dashboard's app list from the central catalog.
  //
  // Per-app stat strings (open-defect count, unread-message count) are
  // computed dynamically; everything else (id/name/icon/path) comes from
  // the SUITE_APPS constant. Single source of truth means we don't have
  // to remember to add new apps in two places.
  const getAppStat = (appId) => {
    if (appId === 'defect-tracker') {
      return stats.loading ? '...' : `${stats.openDefects} open`;
    }
    if (appId === 'messaging') {
      return unreadMessageCount > 0 ? `${unreadMessageCount} unread` : 'Active';
    }
    return 'Active';
  };

  // Build 25.1: two-tier filter — org must enable AND user must be assigned.
  // Admins no longer bypass; they're subject to the same checks so they can
  // curate their own dashboard. /admin remains always-accessible via a
  // separate path (handled below).
  const accessibleApps = SUITE_APPS
    .filter(a => userCanAccessApp(userProfile, a.id, orgSettings))
    .map(a => ({
      ...a,
      stat: getAppStat(a.id),
      enabled: hasProfile,
      badge: a.id === 'messaging' ? unreadMessageCount : undefined
    }));

  // "Coming Soon" placeholders shown to everyone (they're not real apps, so
  // there's nothing to gate).
  const comingSoonApps = COMING_SOON_APPS.map(a => ({
    ...a,
    stat: 'Coming Soon',
    enabled: false,
    comingSoon: true
  }));

  // Admin card — visible only to admins, slot at the top of the list so
  // it's easy to find.
  const adminCard = isAdmin ? [{
    id: 'admin',
    name: 'Admin',
    icon: '⚙️',
    description: 'Manage users, applications, and per-user suite access',
    path: '/admin',
    active: true,
    stat: 'Admin',
    enabled: true,
    isAdminCard: true
  }] : [];

  const apps = [...adminCard, ...accessibleApps, ...comingSoonApps];

  const handleAppClick = (app) => {
    if (!app.enabled) return;
    if (app.comingSoon) {
      alert(`${app.name} is coming soon! Stay tuned for updates.`);
      return;
    }
    if (app.path) {
      navigate(app.path);
    }
  };

  return (
    <div className="suite-dashboard">
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-brand">
            <div className="breadcrumb-nav-dashboard">
              <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>
                🖥️ Desktop
              </button>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-item breadcrumb-current">📦 Dashboard</span>
            </div>
          </div>
          <div className="dashboard-user-section">
            <div className="user-info">
              <span className="user-email">{user.email}</span>
              <span className="user-role-badge">{role}</span>
            </div>
            <button className="btn-signout" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="dashboard-container">
          {accessFlash && (
            <div className="access-flash-banner" role="alert">
              <span className="access-flash-icon">🔒</span>
              <span className="access-flash-text">
                You don't have access to <strong>{accessFlash}</strong>. Contact an administrator if you need access.
              </span>
              <button
                className="access-flash-close"
                onClick={() => setAccessFlash(null)}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          )}

          <div className="dashboard-welcome">
            <h2>Welcome back!</h2>
            <p>Select an application to get started</p>
          </div>

          <div className="apps-grid">
            {apps.map(app => (
              <div
                key={app.id}
                className={`app-card${!app.enabled ? ' app-card--disabled' : ''}${app.active ? ' app-card--active' : ''}${app.isAdminCard ? ' app-card--admin' : ''}`}
                onClick={() => handleAppClick(app)}
              >
                <div className="app-card-icon">
                  {app.icon}
                  {app.badge > 0 && (
                    <div className="app-card-badge">{app.badge > 99 ? '99+' : app.badge}</div>
                  )}
                </div>
                <h3 className="app-card-name">{app.name}</h3>
                <p className="app-card-description">{app.description}</p>
                <div className="app-card-footer">
                  <span className={`app-stat${!app.enabled ? ' app-stat--disabled' : ''}`}>
                    {app.stat}
                  </span>
                  {app.isAdminCard ? (
                    <span className="app-admin-badge">Admin</span>
                  ) : (
                    <>
                      {app.active && app.enabled && (
                        <span className="app-active-badge">Active</span>
                      )}
                      {!app.active && (
                        <span className="app-coming-soon">Coming Soon</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {!hasProfile && (
            <div className="no-access-message">
              <p>⚠️ Your account is awaiting role assignment. Please contact an administrator to access applications.</p>
            </div>
          )}

          {hasProfile && !isAdmin && accessibleApps.length === 0 && (
            <div className="no-access-message">
              <p>🔒 You don't have access to any suite apps yet. Please contact an administrator to be granted access.</p>
            </div>
          )}
        </div>
      </main>

      <footer className="dashboard-footer">
        <p>&copy; 2026 Wixon & Cross LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default SuiteDashboard;
